import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { tap, catchError, map, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { User } from '../../features/users/models/user.models';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import {
  LoginRequest,
  PinLoginRequest,
  QrLoginRequest,
  SetupPinRequest,
  AuthResponse,
  RefreshTokenResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  LogoutRequest,
  QrLoginData,
  QrLoginResult,
} from '../models/auth.models';
import { environment } from '@environments/environment';
import { MatDialog } from '@angular/material/dialog';

interface WebSocketMessage {
  type: string;
  data: any;
}
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private wsConnection: WebSocketSubject<WebSocketMessage> | null = null;
  private apiUrl = environment.apiUrl; // Update with actual API URL

  constructor(
    private router: Router,
    private http: HttpClient,
    private dialog: MatDialog,
  ) {
    // Check for stored auth token on init
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      this.currentUserSubject.next(JSON.parse(storedUser));
    }
  }

  login(email: string, password: string): Observable<User> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/v1/auth/login`, { email, password }).pipe(
      tap((response) => {
        // Store JWT token
        localStorage.setItem('token', response.token);
      }),
      switchMap((response) => this.fetchUserProfile(response.token)),
      catchError((error) => {
        return throwError(
          () => new Error('Login failed: ' + (error.error?.message || error.message)),
        );
      }),
    );
  }

  loginWithPin(mobile: string, pin: string): Observable<User> {
    const pinRequest: PinLoginRequest = {
      phoneNumber: mobile,
      pin: pin,
    };

    return this.http.post<AuthResponse>(`${this.apiUrl}/v1/auth/login-with-pin`, pinRequest).pipe(
      tap((response) => {
        // Store JWT token
        localStorage.setItem('token', response.token);
      }),
      switchMap((response) => this.fetchUserProfile(response.token)),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      }),
    );
  }

  connectQRWebSocket(sessionId: string): Observable<WebSocketMessage> {
    // In a real app, this would connect to your actual WebSocket server
    const wsUrl = `ws://localhost:8080/qr-auth/${sessionId}`;

    if (!this.wsConnection) {
      this.wsConnection = webSocket<WebSocketMessage>({
        url: wsUrl,
        deserializer: (msg) => ({
          type: 'message',
          data: msg.data,
        }),
        serializer: (msg) => JSON.stringify(msg),
      });
    }

    return this.wsConnection.asObservable();
  }

  closeQRWebSocket() {
    if (this.wsConnection) {
      this.wsConnection.complete();
      this.wsConnection = null;
    }
  }

  setupPin(pin: string, expirationDays: number = 0): Observable<string> {
    const request: SetupPinRequest = {
      pin: pin,
      expirationDays: expirationDays,
    };

    return this.http.post<string>(`${this.apiUrl}/auth/setup-pin`, request);
  }

  disablePin(): Observable<string> {
    return this.http.post<string>(`${this.apiUrl}/auth/disable-pin`, {});
  }

  logout() {
    // Close any open dialogs immediately to ensure a clean state
    this.dialog.closeAll();

    // 1) trigger the navigation while this component is still mounted
    this.router.navigate(['/auth/login'], { replaceUrl: true })
      .then(() => {
        // 2) only once we’re "on" /auth/login do we clear everything
        localStorage.removeItem('currentUser');
        localStorage.removeItem('token');
        this.currentUserSubject.next(null);
        this.closeQRWebSocket();
        // Extra safety: close dialogs again after navigation
        this.dialog.closeAll();
      });
  }

  isAuthenticated(): boolean {
    return !!this.currentUserSubject.value;
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  // Method to get user profile from the API
  getUserProfile(token: string): Observable<User> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http.get<User>(`${this.apiUrl}/v1/user/me`, { headers: headers }).pipe(
      catchError((error) => {
        return throwError(
          () =>
            new Error('Failed to fetch user profile: ' + (error.error?.message || error.message)),
        );
      }),
    );
  }

  // Helper method to fetch user profile and update local state
  private fetchUserProfile(token: string): Observable<User> {
    console.log('Fetching user profile with token:', token);
    return this.getUserProfile(token).pipe(
      tap((user) => {
        // Save user info to local storage and notify subscribers
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.currentUserSubject.next(user);
      }),
    );
  }


  validateQrLogin(username: string, code: string): Observable<QrLoginResult> {
    return this.http
      .post<QrLoginResult>(`${this.apiUrl}/qrlogin/validate`, {
        username,
        code,
      })
      .pipe(
        tap((result) => {
          if (result.success && result.token) {
            localStorage.setItem('token', result.token);
          }
        }),
      );
  }

  loginWithQRT2(qrToken: string): Observable<User> {
    // Parse the QR token
    const tokenParts = qrToken.split('-');
    console.log(tokenParts, qrToken);

    if (tokenParts.length !== 3 || tokenParts[1] !== 'TECH') {
      return throwError(() => new Error('Invalid technician QR code'));
    }

    const raw = tokenParts[2];
    const decoded = decodeURIComponent(raw);
    const [username, query] = decoded.split('?');
    const [signature, _] = query.split('&issuer');

    return this.http
      .post<AuthResponse>(`${this.apiUrl}/v1/Auth/validate/qr-code`, {
        username: username,
        code: signature.replace('secret=', ''),
      })
      .pipe(
        tap((response) => {
          // Store JWT token
          localStorage.setItem('token', response.token);
        }),
        switchMap((response) => this.fetchUserProfile(response.token)),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }
}
